/**
 * <GovtIdUploader> — reusable government-ID / passport document uploader.
 * Persists to the shared `/api/media/v1/upload` endpoint (backed by Emergent
 * Object Storage) and stores the returned URL. Used by:
 *  - Walk-In drawer
 *  - New Reservation dialog
 *  - Reservation Edit dialog
 *
 * Contract:
 *   value    — the currently stored media URL (or "")
 *   onChange — receives the new URL on upload, "" on remove
 *   testid   — data-testid prefix (dropzone/input/preview all namespaced)
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
const MAX_MB = 10;

export default function GovtIdUploader({ value, onChange, testid = "govtid-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [meta, setMeta] = useState(null);

  const isPdf = (meta?.content_type === "application/pdf") ||
                 (typeof value === "string" && value.toLowerCase().endsWith(".pdf"));

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Only images (JPG, PNG, WebP) or PDF are allowed");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB limit`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/v1/upload", fd);
      onChange(data.url);
      setMeta({ filename: data.filename, content_type: data.content_type });
      toast.success("ID document attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => { onChange(""); setMeta(null); if (inputRef.current) inputRef.current.value = ""; };

  if (value) {
    return (
      <div data-testid={`${testid}-preview`}
            className="rounded-lg border-2 border-slate-200 bg-slate-50 p-2.5 flex items-center gap-2.5">
        {isPdf ? (
          <div className="w-12 h-12 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-500">
            <FileText className="w-5 h-5"/>
          </div>
        ) : (
          <img src={value} alt="Guest ID"
                className="w-12 h-12 rounded-md object-cover border border-slate-200 bg-white"
                onError={(e) => { e.currentTarget.style.display = "none"; }}/>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-medium text-slate-700 truncate">
            {meta?.filename || (isPdf ? "ID Document" : "ID Photo attached")}
          </div>
          <a href={value} target="_blank" rel="noopener noreferrer"
              className="text-[11px] text-sky-700 hover:underline mono truncate block">
            View file
          </a>
        </div>
        <button type="button" onClick={clear}
                data-testid={`${testid}-remove`}
                className="h-8 px-2 rounded-md text-slate-500 hover:bg-slate-200 inline-flex items-center gap-1 text-[12px]">
          <X className="w-3.5 h-3.5"/> Remove
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`${testid}-dropzone`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer rounded-lg border-2 border-dashed transition px-3 py-4 text-center ${
            dragOver
              ? "border-slate-800 bg-slate-100"
              : "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100"
          }`}>
      <input ref={inputRef} type="file" accept={ACCEPT}
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden" data-testid={`${testid}-input`}/>
      {uploading ? (
        <div className="inline-flex items-center gap-2 text-[13px] text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin"/> Uploading…
        </div>
      ) : (
        <>
          <Upload className="w-5 h-5 mx-auto text-slate-400 mb-1"/>
          <div className="text-[13px] font-medium text-slate-700">Upload ID · click or drag &amp; drop</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Passport / Aadhaar / Driver's License · JPG, PNG or PDF · ≤ {MAX_MB} MB</div>
        </>
      )}
    </div>
  );
}
